/**
 * Semáforo de una línea de kit en el puesto de almacén.
 *
 * El margen de 0,0005 es el mismo que usa la API (`scanned_qty + 0.0005 >= required_qty`)
 * para dar una línea por completa: sin él, escanear 2,4 m² a un requerimiento de 2,4 m²
 * podía quedar en naranja por el redondeo de la coma flotante.
 */
export type TrafficLight = 'empty' | 'partial' | 'full';

export const TRAFFIC_EPSILON = 0.0005;

export function trafficLight(requiredQty: number, scannedQty: number): TrafficLight {
  // Línea sin cantidad requerida (informativa): no falta nada, así que está completa y no se
  // queda en blanco para siempre.
  if (!(requiredQty > 0)) return 'full';
  if (!(scannedQty > 0)) return 'empty';
  return scannedQty + TRAFFIC_EPSILON >= requiredQty ? 'full' : 'partial';
}

/** Semáforo de una línea del kit, con la forma que devuelve la API. */
export function lineTraffic(line: { requiredQty: number; scannedQty: number }): TrafficLight {
  return trafficLight(line.requiredQty, line.scannedQty);
}

/**
 * Se escaneó más de lo requerido. La API solo rechaza a partir de 5×, así que un exceso menor
 * se acepta: el puesto de almacén tiene que avisarlo igual, porque `actualQty` queda por encima
 * de lo pedido.
 */
export function isOverScanned(line: { requiredQty: number; scannedQty: number }): boolean {
  return line.scannedQty > line.requiredQty + TRAFFIC_EPSILON;
}

/** Clases tailwind del semáforo: blanco (nada), naranja (parcial), verde (completo). */
export const TRAFFIC_CLASSES: Record<TrafficLight, { dot: string; row: string; label: string }> = {
  empty: { dot: 'bg-white border border-slate-400', row: 'border-slate-200 bg-white', label: 'text-slate-600' },
  partial: { dot: 'bg-amber-500', row: 'border-amber-300 bg-amber-50', label: 'text-amber-800' },
  full: { dot: 'bg-emerald-500', row: 'border-emerald-300 bg-emerald-50', label: 'text-emerald-800' }
};

/** Antigüedad legible para el operario: «3 h», «2 d 4 h». */
export function formatAge(hours: number): string {
  if (!Number.isFinite(hours) || hours < 0) return '—';
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} min`;
  if (hours < 48) return `${Math.round(hours)} h`;
  const days = Math.floor(hours / 24);
  const rest = Math.round(hours - days * 24);
  return rest > 0 ? `${days} d ${rest} h` : `${days} d`;
}

/** El más viejo primero: es el orden en el que el puesto de almacén debe atacar la cola. */
export function oldestFirst<T extends { created_at: string }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
}

/** Piezas pendientes de un kit del listado (`pending_lines` viene de la API). */
export function pendingPieces(pendingLines: number | null | undefined): number {
  return Number.isFinite(pendingLines) ? Math.max(0, Number(pendingLines)) : 0;
}

/**
 * Cantidad tecleada en un puesto de almacén español: «2,4» es dos coma cuatro, no un error.
 * Devuelve null si no hay un número positivo que enviar; la pantalla lo avisa en vez de
 * sustituirlo por 1 a escondidas (lo que descuadraría justo las líneas en m²).
 */
export function parseQuantity(raw: string): number | null {
  const normalized = raw.trim().replace(',', '.');
  if (!normalized) return null;
  const value = Number(normalized);
  if (!Number.isFinite(value) || value <= 0) return null;
  return value;
}

/**
 * Firma del exceso de un kit: qué líneas se pasaron y cuánto. Comparar la firma (en vez de un
 * booleano) hace que un exceso nuevo —por un escaneo local o por uno de otro puesto que llega por
 * SSE— vuelva a exigir el reconocimiento del operario.
 */
export function excessSignature(
  lines: { id?: string; sku?: string; requiredQty: number; scannedQty: number }[]
): string {
  return lines
    .filter(isOverScanned)
    .map((line) => `${line.id ?? line.sku}:${(line.scannedQty - line.requiredQty).toFixed(3)}`)
    .sort()
    .join('|');
}

/** El cierre exige reconocer el exceso actual: sin exceso no hay nada que reconocer. */
export function needsExcessAck(
  lines: { id?: string; sku?: string; requiredQty: number; scannedQty: number }[],
  acknowledged: string | null
): boolean {
  const signature = excessSignature(lines);
  return signature !== '' && signature !== acknowledged;
}

/**
 * Último escaneo vivo de una línea: es el que se deshace cuando el lector dispara dos veces.
 * Se empareja por `kitLineId` y no por producto, porque el escaneo de una sustitución guarda el
 * artículo alternativo y colgaría de otra línea si se buscase por producto.
 */
export function lastLiveScanForLine<T extends { kitLineId?: string; voidedAt?: string | null }>(
  scans: T[],
  lineId: string
): T | null {
  const live = scans.filter((scan) => scan.kitLineId === lineId && !scan.voidedAt);
  return live.length ? live[live.length - 1]! : null;
}
